/**
 * ADR-0068: Scope-aware Menus list at /app/workspace/menus.
 * Editor stays at /app/build/menus/:menuId.
 */

import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PageLayout } from "../components/PageLayout.js";
import { ErrorState, LoadingState } from "../components/States.js";
import { ScopePill } from "../components/ScopePill.js";
import { ScopedRowActionsMenu } from "../components/ScopedRowActionsMenu.js";
import { ScopeTabs, useScopeTabFromUrl, useResolvedScopeTab, useScopeTabsBasePath } from "../components/ScopeTabs.js";
import { useToast } from "../components/Toast.js";
import { apiFetch, ApiRequestError } from "../lib/api.js";
import { useMyGroups, type ContentScope, type ScopeCounts, type ScopedEntityFields, useMyAccountId } from "../lib/scope.js";

type ScopedMenuListRow = {
  id: string;
  tenant_id: string;
  name: string;
  status: "published" | "active" | "draft" | "inactive";
  created_at: string;
  updated_at: string;
} & ScopedEntityFields;

export function WorkspaceMenus({ callerAccountId, tenantId, entitlement }: { callerAccountId: string; tenantId: string; entitlement: "viewer" | "builder" | "admin" }) {
  const nav = useNavigate();
  const toast = useToast();
  const tab = useScopeTabFromUrl();
  const basePath = useScopeTabsBasePath();
  const activeTab = useResolvedScopeTab({ tenantId, accountId: callerAccountId, entity: "menus" });
  const { groups } = useMyGroups();
  const myAccountId = useMyAccountId();
  const inZeroGroups = groups.length === 0 && entitlement !== "admin";

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<ScopedMenuListRow[]>([]);
  const [counts, setCounts] = useState<ScopeCounts | null>(null);
  const [q, setQ] = useState("");

  const [showAddModal, setShowAddModal] = useState(false);
  const [newName, setNewName] = useState("");
  const [newScope, setNewScope] = useState<ContentScope>("personal");
  const [newGroupId, setNewGroupId] = useState("");

  const extraSearch = useMemo(() => {
    const p = new URLSearchParams();
    if (q.trim()) p.set("q", q.trim());
    return p;
  }, [q]);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set("tab", activeTab);
      if (q.trim()) params.set("q", q.trim());
      const [listRes, countsRes] = await Promise.all([
        apiFetch<{ menus: ScopedMenuListRow[] }>(`/api/tenant/menus?${params.toString()}`),
        apiFetch<{ counts: ScopeCounts }>(`/api/tenant/menus?counts_only=1`)
      ]);
      setRows(listRes.menus);
      setCounts(countsRes.counts);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [activeTab]);
  useEffect(() => { const t = setTimeout(() => { void load(); }, 300); return () => clearTimeout(t); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [q]);

  async function createMenu() {
    if (!newName.trim()) return;
    setSaving(true);
    try {
      const body: Record<string, unknown> = { name: newName.trim(), scope: newScope };
      if (newScope === "team") body.owner_group_id = newGroupId;
      const data = await apiFetch<{ menu: { id: string } }>("/api/tenant/menus", { method: "POST", body });
      setNewName(""); setNewScope("personal"); setNewGroupId(""); setShowAddModal(false);
      nav(`/app/build/menus/${encodeURIComponent(data.menu.id)}`);
    } catch (e) {
      const msg = e instanceof ApiRequestError ? e.message : e instanceof Error ? e.message : String(e);
      toast.push({ kind: "error", message: msg });
    } finally {
      setSaving(false);
    }
  }

  const canCreateTeam = groups.length > 0 || entitlement === "admin";
  const canCreateShared = entitlement === "builder" || entitlement === "admin";

  return (
    <PageLayout title="Menus">
      <div style={{ display: "grid", gap: 12 }}>
        <ScopeTabs
          currentTab={tab}
          counts={counts}
          basePath={basePath}
          extraSearch={extraSearch}
          inZeroGroups={inZeroGroups}
          storageFingerprint={{ tenantId, accountId: callerAccountId, entity: "menus" }}
        />
        <div className="toolbar">
          <div className="toolbarGroup">
            <input className="input" style={{ width: 260 }} placeholder="Search menus…" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <div className="toolbarGroup">
            <button className="btn btnPrimary" onClick={() => setShowAddModal(true)}>+ New menu</button>
          </div>
        </div>
        {error ? <ErrorState detail={error} /> : null}
        {loading ? (
          <LoadingState />
        ) : rows.length === 0 ? (
          <EmptyMenuState tab={activeTab} inZeroGroups={inZeroGroups} />
        ) : (
          <div className="tableWrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Scope</th>
                  <th>Status</th>
                  <th>Updated</th>
                  <th style={{ width: 40 }} aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td><Link to={`/app/build/menus/${encodeURIComponent(r.id)}`}>{r.name}</Link></td>
                    <td><ScopePill scope={r.scope} groupName={r.owner_group_name} ownerDisplayName={r.owner_display_name} ownerIsViewer={r.owner_account_id === myAccountId} /></td>
                    <td><span className="pill">{r.status}</span></td>
                    <td className="muted">{new Date(r.updated_at).toLocaleDateString()}</td>
                    <td style={{ textAlign: "right" }}>
                      <ScopedRowActionsMenu entityKind="menu" entityId={r.id} entityName={r.name} onDeleted={load} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showAddModal && (
        <div className="modalOverlay" role="presentation" onClick={saving ? undefined : () => setShowAddModal(false)}>
          <div className="modal" role="dialog" aria-modal="true" style={{ maxWidth: 460, width: "90%" }} onClick={(e) => e.stopPropagation()}>
            <div style={{ padding: "14px 18px", borderBottom: "1px solid var(--border)" }}>
              <h2 style={{ margin: 0, fontSize: 16 }}>New menu</h2>
            </div>
            <div style={{ padding: "14px 18px", display: "grid", gap: 12 }}>
              <label className="field">
                <span className="label">Name</span>
                <input className="input" autoFocus value={newName} onChange={(e) => setNewName(e.target.value)} />
              </label>
              <div className="field">
                <span className="label">Scope</span>
                <div className="scopeCreateRadio">
                  <label className={`scopeCreateRadioOption${newScope === "personal" ? " isActive" : ""}`}>
                    <input type="radio" name="scope" value="personal" checked={newScope === "personal"} onChange={() => setNewScope("personal")} />
                    <div><div className="scopeCreateRadioLabel">Personal</div><div className="scopeCreateRadioHelp">You and your management chain.</div></div>
                  </label>
                  <label className={`scopeCreateRadioOption${newScope === "team" ? " isActive" : ""}${!canCreateTeam ? " isDisabled" : ""}`}>
                    <input type="radio" name="scope" value="team" checked={newScope === "team"} disabled={!canCreateTeam} onChange={() => setNewScope("team")} />
                    <div><div className="scopeCreateRadioLabel">Team</div><div className="scopeCreateRadioHelp">{canCreateTeam ? "Shared with one team." : "Ask an admin to add you to a team."}</div></div>
                  </label>
                  {canCreateShared && (
                    <label className={`scopeCreateRadioOption${newScope === "shared" ? " isActive" : ""}`}>
                      <input type="radio" name="scope" value="shared" checked={newScope === "shared"} onChange={() => setNewScope("shared")} />
                      <div><div className="scopeCreateRadioLabel">Shared</div><div className="scopeCreateRadioHelp">Visible to the whole tenant.</div></div>
                    </label>
                  )}
                </div>
              </div>
              {newScope === "team" && (
                <label className="field">
                  <span className="label">Team</span>
                  <select className="select" value={newGroupId} onChange={(e) => setNewGroupId(e.target.value)}>
                    <option value="">Select a team…</option>
                    {groups.map((g) => (<option key={g.id} value={g.id}>{g.name}</option>))}
                  </select>
                </label>
              )}
            </div>
            <div style={{ padding: "12px 18px", borderTop: "1px solid var(--border)", display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button className="btn" onClick={() => setShowAddModal(false)} disabled={saving}>Cancel</button>
              <button className="btn btnPrimary" onClick={() => void createMenu()} disabled={saving || !newName.trim() || (newScope === "team" && !newGroupId)}>{saving ? "Creating…" : "Create"}</button>
            </div>
          </div>
        </div>
      )}
    </PageLayout>
  );
}

function EmptyMenuState(props: { tab: "mine" | "teams" | "shared"; inZeroGroups: boolean }) {
  if (props.tab === "mine") return <div className="muted" style={{ padding: "24px 12px" }}>You haven't created any personal menus yet.</div>;
  if (props.tab === "teams") {
    if (props.inZeroGroups) return <div className="muted" style={{ padding: "24px 12px" }}>You are not a member of any team yet.</div>;
    return <div className="muted" style={{ padding: "24px 12px" }}>No team menus in your teams.</div>;
  }
  return <div className="muted" style={{ padding: "24px 12px" }}>No shared menus in this tenant yet.</div>;
}
